import { headings, movingAverage, projectionOrigin, projectLap, turnRate } from "./geo";
import type { AlignedLap } from "./trace";

export interface Corner {
  /** 1-based, in distance order around the lap. */
  number: number;
  direction: "left" | "right";
  /** Indices into the shared distance grid. */
  startIndex: number;
  apexIndex: number;
  endIndex: number;
  /** Widened back through the braking zone; the range worth zooming to. */
  focusStartIndex: number;
  /** Total heading change across the corner, in degrees. */
  turnedDegrees: number;
  peakLateralG: number;
  minSpeedKph: number | null;
}

/** Smoothing applied to the heading before differentiating it. */
const HEADING_SMOOTHING_M = 45;
/** Half-width of the window the turn rate is measured over. */
const CURVATURE_SPAN_M = 25;

/** Below this the track is bending, not turning. */
const MIN_PEAK_DEG_PER_M = 0.2;

/**
 * Two apexes closer than this turning the same way are treated as one corner. Applied
 * only within a direction, because the halves of a chicane sit close together and both
 * are real.
 */
const MIN_APEX_SEPARATION_M = 150;

/** A corner ends where the turn rate drops to this share of its own peak. */
const EDGE_FRACTION = 0.4;

/** Final sanity filter: a corner has to actually change where the car is pointing. */
const MIN_TURN_DEGREES = 20;

/** How far back to look for the braking zone that belongs to a corner. */
const BRAKE_LOOKBACK_M = 250;
const BRAKE_ON_PCT = 10;
/** Tolerates a brief release inside one braking event without ending it. */
const BRAKE_GAP_M = 20;

/**
 * Finds the corners of a lap from the curvature of its GPS path.
 *
 * Curvature rather than lateral G, which is the more obvious choice and was tried
 * first: lateral G describes how a lap was *driven*, so the corners it finds move when
 * the driving changes. Detected independently across twelve laps of one session, the
 * lateral-G version disagreed with itself by three corners and put 16 to 33 of its
 * windows on stretches that turn the car less than 20°, including some on outright
 * straights. Curvature describes the track, which does not change: the same test gives
 * a spread of one corner and a single sub-20° window. As a cross-check the direction it
 * assigns agreed with the sign of lateral G on all 121 corners found across those laps.
 *
 * Detection anchors on peaks of the turn rate and walks outward, rather than
 * thresholding, because a threshold cannot separate two corners of the same direction
 * taken in sequence: widening and merging turn them into one 450 m blob.
 *
 * Peak G and minimum speed come from the raw channels inside the detected window, since
 * the smoothing that makes segmentation reliable also flattens peaks by around 15%.
 *
 * Detect once, on a single reference lap, and reuse the windows for every lap being
 * compared, so that a corner means the same stretch of track for all of them.
 */
export function detectCorners(lap: AlignedLap, distanceStepM: number): Corner[] {
  if (!Number.isFinite(distanceStepM) || distanceStepM <= 0) return [];

  const origin = projectionOrigin(lap);
  if (!origin) return [];
  const points = projectLap(lap, origin);
  if (!points) return [];

  const heading = movingAverage(
    headings(points),
    Math.max(3, Math.round(HEADING_SMOOTHING_M / distanceStepM)),
  );
  const rate = turnRate(
    heading,
    distanceStepM,
    Math.max(1, Math.round(CURVATURE_SPAN_M / distanceStepM)),
  );

  const magnitude = (index: number) => {
    const value = rate[index];
    return value === null ? null : Math.abs(value);
  };

  const candidates: number[] = [];
  for (let i = 1; i < rate.length - 1; i += 1) {
    const here = magnitude(i);
    const before = magnitude(i - 1);
    const after = magnitude(i + 1);
    if (here === null || before === null || after === null) continue;
    if (here >= MIN_PEAK_DEG_PER_M && here >= before && here > after) candidates.push(i);
  }

  const separation = MIN_APEX_SEPARATION_M / distanceStepM;
  const apexes: number[] = [];
  // Strongest first, so the peak that survives a cluster is the real apex.
  for (const index of [...candidates].sort(
    (a, b) => (magnitude(b) ?? 0) - (magnitude(a) ?? 0),
  )) {
    const left = (rate[index] as number) > 0;
    const crowded = apexes.some(
      (kept) => Math.abs(index - kept) < separation && (rate[kept] as number) > 0 === left,
    );
    if (!crowded) apexes.push(index);
  }
  apexes.sort((a, b) => a - b);

  const lateral = lap.channels.g_lat ?? [];
  const brakeTrace = lap.channels.brake ?? [];
  const speed = lap.channels.speed ?? [];

  const corners: Corner[] = [];
  for (const apex of apexes) {
    const peak = magnitude(apex) as number;
    const floor = Math.max(MIN_PEAK_DEG_PER_M * 0.55, EDGE_FRACTION * peak);
    const left = (rate[apex] as number) > 0;

    const withinCorner = (index: number) => {
      const value = rate[index];
      if (value === null) return false;
      if (Math.abs(value) < floor) return false;
      return value > 0 === left;
    };

    // Stop where the turn rate starts building again: that is the handover to the next
    // corner, not part of this one.
    let startIndex = apex;
    while (startIndex > 0) {
      const next = startIndex - 1;
      if (!withinCorner(next)) break;
      if ((magnitude(next) as number) > (magnitude(startIndex) as number)) break;
      startIndex = next;
    }

    let endIndex = apex;
    while (endIndex < rate.length - 1) {
      const next = endIndex + 1;
      if (!withinCorner(next)) break;
      if ((magnitude(next) as number) > (magnitude(endIndex) as number)) break;
      endIndex = next;
    }

    const from = heading[startIndex];
    const to = heading[endIndex];
    if (from === null || to === null) continue;
    const turnedDegrees = Math.abs(((to - from) * 180) / Math.PI);
    if (turnedDegrees < MIN_TURN_DEGREES) continue;

    // Reach back through this corner's braking zone, but never past the previous
    // corner's exit, or the window would swallow the corner before it.
    const previous = corners[corners.length - 1];
    const limit = Math.max(
      previous ? previous.endIndex + 1 : 0,
      startIndex - Math.round(BRAKE_LOOKBACK_M / distanceStepM),
    );
    const gapAllowance = Math.round(BRAKE_GAP_M / distanceStepM);
    let focusStartIndex = startIndex;
    let released = 0;
    for (let index = startIndex - 1; index >= limit; index -= 1) {
      const brake = brakeTrace[index];
      if (brake !== null && brake !== undefined && brake >= BRAKE_ON_PCT) {
        focusStartIndex = index;
        released = 0;
      } else {
        released += 1;
        if (released > gapAllowance) break;
      }
    }

    let peakLateralG = 0;
    let minSpeedKph: number | null = null;
    let apexIndex = apex;
    for (let index = startIndex; index <= endIndex; index += 1) {
      const raw = lateral[index];
      if (raw !== null && raw !== undefined) {
        peakLateralG = Math.max(peakLateralG, Math.abs(raw));
      }
      const kph = speed[index];
      if (
        kph !== null &&
        kph !== undefined &&
        (minSpeedKph === null || kph < minSpeedKph)
      ) {
        minSpeedKph = kph;
        // The slowest point is more useful to a driver than the peak of a smoothed
        // geometric signal.
        apexIndex = index;
      }
    }

    corners.push({
      number: corners.length + 1,
      direction: left ? "left" : "right",
      startIndex,
      apexIndex,
      endIndex,
      focusStartIndex,
      turnedDegrees,
      peakLateralG,
      minSpeedKph,
    });
  }

  return corners;
}
